// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — "My Khipus" at the top of the Khipus tab (was "My lessons"; every
 * kind since 2026-10-07: lessons, stories, session records): the Khipus
 * someone keeps on their account (`khipuStore`, `/api/khipus`), with when each was
 * saved and until when it is kept; Open plays it in the lesson player,
 * Download saves it as a `.zip`, Delete (pressed twice) removes it. Shown
 * for a plan that keeps Khipus, and, after the licence, for as long as some
 * are left (Download and Delete only, with a line saying why). Drawn into
 * the tab's own markup, so
 * the tab can re-render freely; the list is fetched when the tab is shown
 * and after a change.
 */
import { t } from '../i18n'
import { formatDate } from '../i18n/format'
import { accountState, currentEntitlements } from '../services/account'
import type { Khipu } from '../services/khipu'
import { deleteKeptKhipu, listKeptKhipus, readKeptKhipu, type KeptKhipu } from '../services/khipuStore'
import { zipKhipu } from '../services/lessonService'
import { escapeAttr, escapeHtml } from './domUtils'

export interface MyLessons {
  /** The section's markup, or '' when the plan does not keep Khipus. */
  html(): string
  /** Fetch the list again; resolves once it is known. */
  load(): Promise<void>
  /** Handle a click inside the tab; true when it was this section's. */
  onClick(target: HTMLElement): boolean
}

/** Whether this account's plan keeps Khipus on its account. */
export function keepsKhipus(): boolean {
  return accountState().status === 'signedIn' && currentEntitlements().khipuRetention !== null
}

const day = (ms: number) => formatDate(new Date(ms), { dateStyle: 'medium' })

const KIND_LABEL: Record<string, 'khipu.kind.lesson' | 'khipu.kind.story' | 'khipu.kind.session' | 'khipu.kind.briefing' | 'khipu.kind.research'> = {
  lesson: 'khipu.kind.lesson',
  story: 'khipu.kind.story',
  session: 'khipu.kind.session',
  briefing: 'khipu.kind.briefing',
  research: 'khipu.kind.research',
}

/** The kinds the Khipu editor edits (a lesson has its own panel; records are what happened). */
const EDITABLE = new Set(['story', 'briefing'])

export function createMyLessons(opts: {
  onOpen: (k: Khipu, files: Array<{ name: string; body: string }>) => void
  /** AYNI: open a story or briefing in the Khipu editor. */
  onEdit?: (k: Khipu, files: Array<{ name: string; body: string }>) => void
  onChange: () => void
  announce?: (m: string) => void
}): MyLessons {
  let items: KeptKhipu[] | null = null
  let readOnly = false
  let failed = false
  let armed: string | null = null
  let armTimer: ReturnType<typeof setTimeout> | null = null

  const html = (): string => {
    if (accountState().status !== 'signedIn') return ''
    // A plan that does not keep Khipus shows the section only while some are left.
    if (!keepsKhipus() && !(readOnly && items && items.length > 0)) return ''
    let body: string
    if (failed) body = `<p class="lessons-error">${escapeHtml(t('lessons.mine.error'))}</p>`
    else if (items === null) body = `<p class="lessons-loading">${escapeHtml(t('lessons.loading'))}</p>`
    else if (items.length === 0) body = `<p class="lessons-hint">${escapeHtml(t('lessons.mine.empty'))}</p>`
    else {
      body = `<ul class="my-lessons-list" role="list">${items.map((k) => `
        <li class="my-lesson" data-id="${escapeAttr(k.id)}">
          <span class="my-lesson-title">${escapeHtml(k.title)}</span>
          <span class="lessons-card-meta">${escapeHtml([KIND_LABEL[k.kind] ? t(KIND_LABEL[k.kind]) : '', t('lessons.mine.meta', { saved: day(k.savedAt), kept: day(k.keptUntil) })].filter(Boolean).join(' · '))}</span>
          <span class="lessons-actions">
            ${readOnly ? '' : `<button type="button" class="pl-tab-btn my-lesson-open" data-id="${escapeAttr(k.id)}">${escapeHtml(t('lessons.mine.open'))}</button>`}
            ${readOnly || !opts.onEdit || !EDITABLE.has(k.kind) ? '' : `<button type="button" class="pl-tab-btn pl-tab-btn-quiet my-lesson-edit" data-id="${escapeAttr(k.id)}">${escapeHtml(t('lessons.mine.edit'))}</button>`}
            <button type="button" class="pl-tab-btn pl-tab-btn-quiet my-lesson-download" data-id="${escapeAttr(k.id)}">${escapeHtml(t('lessons.mine.download'))}</button>
            <button type="button" class="pl-tab-btn pl-tab-btn-quiet my-lesson-delete${armed === k.id ? ' armed' : ''}" data-id="${escapeAttr(k.id)}">${escapeHtml(t(armed === k.id ? 'lessons.mine.confirmDelete' : 'lessons.mine.delete'))}</button>
          </span>
        </li>`).join('')}</ul>`
      if (readOnly) body = `<p class="lessons-hint">${escapeHtml(t('lessons.mine.ended'))}</p>${body}`
    }
    return `<section class="my-lessons" aria-labelledby="my-lessons-heading"><h3 class="my-lessons-heading" id="my-lessons-heading">${escapeHtml(t('lessons.mine.heading'))}</h3>${body}</section>`
  }

  const load = async (): Promise<void> => {
    if (accountState().status !== 'signedIn') return
    const result = await listKeptKhipus()
    // 402: nothing kept, and a plan that keeps nothing; 503: not here.
    failed = !result.ok && result.error !== 'not_configured' && result.error !== 'plan_required'
    items = result.ok ? result.value.items : []
    readOnly = result.ok && result.value.readOnly
  }

  const download = async (id: string): Promise<void> => {
    const read = await readKeptKhipu(id)
    if (!read.ok) return
    const { blob, filename } = await zipKhipu(read.value.khipu, read.value.files)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }

  const onClick = (target: HTMLElement): boolean => {
    const button = target.closest<HTMLElement>('.my-lesson-open, .my-lesson-edit, .my-lesson-download, .my-lesson-delete')
    const id = button?.dataset.id
    if (!button || !id) return false
    if (button.classList.contains('my-lesson-open')) {
      void readKeptKhipu(id).then((read) => {
        if (read.ok) opts.onOpen(read.value.khipu, read.value.files ?? [])
      })
    } else if (button.classList.contains('my-lesson-edit')) {
      void readKeptKhipu(id).then((read) => {
        if (read.ok) opts.onEdit?.(read.value.khipu, read.value.files ?? [])
      })
    } else if (button.classList.contains('my-lesson-download')) {
      void download(id)
    } else if (armed !== id) {
      // A first press asks; a second within four seconds deletes.
      armed = id
      if (armTimer) clearTimeout(armTimer)
      armTimer = setTimeout(() => {
        armed = null
        opts.onChange()
      }, 4000)
      opts.onChange()
    } else {
      armed = null
      void deleteKeptKhipu(id).then(async () => {
        await load()
        opts.announce?.(t('lessons.mine.deleted'))
        opts.onChange()
      })
    }
    return true
  }

  return { html, load, onClick }
}
